/* eslint-disable react/no-this-in-sfc */
import React, { useContext, useState, useEffect } from 'react';
import { Container, Row, Col } from 'reactstrap';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikErrors, FormikTouched } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router';
import OperationContext from '../../context';
import NotificationContext from '../../../../Notifications/NotificationContext';
import EsrrContext from '../EsrrContext';
import {
  PageHeader,
  Section,
  Button,
  ConfirmModal,
  SpinnerArea,
} from '../../../../../shared/components';
import TextArea from '../../../../../shared/components/form/TextArea';

import {
  OperationEsrrQuestions,
  OperationEsrrAnswer,
  OperationEsrrAnswers,
} from '../../../../../services/types/Esrr';
import Context from '../../../../App/MainProvider/context';
import operEsrrService from '../../../../../services/operEsrrService';
import FormRadio from '../../../../../shared/components/form/FormRadio';
import { IdRoute } from '../../../../../types';

type esrrQuestionOption = {
  id: number;
  value: string;
  text: string;
};

const esrrOptions: esrrQuestionOption[] = [
  { id: 1, value: '1Low', text: 'L' },
  { id: 2, value: '2Moderate', text: 'M' },
  { id: 3, value: '3Substantial', text: 'S' },
  { id: 4, value: '4High', text: 'H' },
];

const EsrrQuestionForm: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: esrrId } = useParams() as unknown as IdRoute;
  const { t } = useTranslation();
  const { fromLast }: { fromLast?: boolean } = (state as { fromLast?: boolean }) || {
    fromLast: undefined,
  };
  const {
    operation: { id, number },
    reload: reloadOperation,
  } = useContext(OperationContext);
  const { reload: reloadNotifications } = useContext(NotificationContext);
  const { questions } = useContext(EsrrContext);
  const { service, notify, inSuperUserMode, inGuidanceServiceMode } = useContext(Context);

  const [esrrAnswers, setEsrrAnswers] = useState<OperationEsrrQuestions[] | null>(null);
  const [lastAnswers, setLastAnswers] = useState<OperationEsrrAnswers | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState(true);
  const showSaveModal = (answers: OperationEsrrQuestions[]) => setEsrrAnswers(answers);
  const closeSaveModal = () => setEsrrAnswers(null);

  const operationContext = useContext(OperationContext);
  const { operation } = operationContext;

  const canEdit = operation.isMember || inSuperUserMode() || inGuidanceServiceMode();

  useEffect(() => {
    if (esrrId) {
      service(() => operEsrrService.getAnswers(parseInt(esrrId, 10)), {
        callback: (data) => {
          setLastAnswers({ operationId: id, answers: data });
          setReadOnly(true);
        },
      });
    } else if (fromLast) {
      service(() => operEsrrService.getLatestAnswers(id), {
        callback: (data) => {
          setLastAnswers({ operationId: id, answers: data });
          setReadOnly(false);
        },
      });
    } else {
      setLastAnswers({ operationId: id, answers: null });
      setReadOnly(false);
    }
  }, []);

  const saveEsrrAnswers = () => {
    if (!esrrAnswers) {
      return;
    }

    setLoader(true);

    const formattedAnswers: OperationEsrrAnswer[] = esrrAnswers.map(
      (esrrAnswer) =>
        ({
          questionId: esrrAnswer.id,
          status: esrrAnswer.option || null,
          comment: esrrAnswer.comment || null,
        } as OperationEsrrAnswer)
    );

    const payload: OperationEsrrAnswers = {
      operationId: id,
      answers: formattedAnswers,
    };

    const callback = () => {
      setEsrrAnswers(null);
      setLoader(true);

      notify(t('saveEsrrForm'), t('successSave'), 'success');
      reloadOperation();
      reloadNotifications();
      navigate(`/operations/${operation.number}/esrr`);
    };
    service(() => operEsrrService.saveAnswers(payload), { callback, loader: setLoader });
  };

  const RadioHeaders = (
    <div className="esrr-question__headers">
      {esrrOptions.map((esrrOption: esrrQuestionOption) => (
        <h6 key={esrrOption.id} className="esrr-question__header__text">
          {esrrOption.text}
        </h6>
      ))}
    </div>
  );

  return (
    <Container className="esrr-questions__form">
      <PageHeader title={<>{t('esrrQuestionTitle', { number })}</>} backTimes={esrrId ? 2 : 1} />
      <SpinnerArea loading={loader}>
        {lastAnswers && (
          <Row>
            <Section title={t('esrrQuestionForm')} className="section-form">
              <Container>
                <Row>
                  <Col md={9}>
                    <h6>{t('esrrQuestion')}</h6>
                  </Col>
                  <Col md={3} />
                </Row>
              </Container>

              <Formik
                initialValues={{
                  questions: questions.map((x) => {
                    const answer = lastAnswers.answers
                      ? lastAnswers.answers.find((a) => a.questionId === x.id)
                      : null;
                    return {
                      ...x,
                      comment: answer && answer.comment ? answer.comment : '',
                      option: answer && answer.status ? answer.status : '',
                    };
                  }),
                }}
                validationSchema={Yup.object().shape({
                  questions: Yup.array().of(
                    Yup.object().shape({
                      isMandatory: Yup.boolean(),
                      comment: Yup.string()
                        .max(750, t('validateMaxLength', { len: 750 }))
                        .required(t('required')),
                      option: Yup.string().required(t('required')),
                    })
                  ),
                })}
                onSubmit={(values) => {
                  showSaveModal(values.questions);
                }}
                validateOnChange
              >
                {(formik) => {
                  const { values, errors, dirty, touched } = formik;
                  return (
                    <>
                      <Form className="form">
                        <Container>
                          {values.questions.map((question, index) => (
                            <div key={question.id}>
                              <hr />
                              <Row className="esrr-question">
                                <Col md={9}>
                                  <div className="esrr-question__description">
                                    <div className="esrr-question__header">
                                      <span className="esrr-question__order">
                                        {question.orderNumber}
                                      </span>
                                      <span className="esrr-question__category">
                                        {question.category}
                                      </span>
                                    </div>
                                    <div className="esrr-question__question">
                                      {question.question}
                                    </div>
                                  </div>
                                  <div className="esrr-question__comments">
                                    <div className="questions__comments__title">
                                      {question.id === 5 ? t('esrrRationale') : t('comments')}
                                    </div>
                                    <TextArea
                                      name={`questions[${index}].comment`}
                                      placeholder={t('esrrQuestionAnswerPlaceholder')}
                                      className="esrr-question__comments__content"
                                      textAreaClassName="esrr-question-no-resize"
                                      disabled={readOnly}
                                      scrollable
                                    />
                                  </div>
                                </Col>

                                <Col md={3}>
                                  {RadioHeaders}
                                  <div className="esrr-question__options">
                                    {esrrOptions.map((esrrOption: esrrQuestionOption) => (
                                      <FormRadio
                                        key={`questions_${question.id}_option_${esrrOption.id}`}
                                        name={`questions[${index}].option`}
                                        value={esrrOption.value}
                                        text=""
                                        disabled={readOnly}
                                      />
                                    ))}
                                  </div>
                                  {touched.questions &&
                                    touched.questions[index] &&
                                    (
                                      touched.questions[
                                        index
                                      ] as FormikTouched<OperationEsrrQuestions>
                                    ).option &&
                                    errors.questions &&
                                    errors.questions[index] &&
                                    (
                                      errors.questions[
                                        index
                                      ] as FormikErrors<OperationEsrrQuestions>
                                    ).option && (
                                      <span className="form__form-group-error">
                                        {
                                          (
                                            errors.questions[
                                              index
                                            ] as FormikErrors<OperationEsrrQuestions>
                                          ).option
                                        }
                                      </span>
                                    )}
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <div className="form__form-options">
                            <hr />
                            <Button
                              key="btnCancel"
                              className="btn-secondary"
                              text={t('cancel')}
                              type="button"
                              onClick={() => navigate(-1)}
                              disabled={loader}
                            />
                            {!readOnly && canEdit && (
                              <Button
                                key="btnApplyAndSave"
                                className="btn-primary"
                                text={t('apply')}
                                type="submit"
                                disabled={loader || !dirty}
                              />
                            )}
                          </div>
                        </Container>
                      </Form>

                      <ConfirmModal
                        title={t('saveEsrrForm')}
                        buttonText={t('save')}
                        visible={esrrAnswers != null}
                        close={closeSaveModal}
                        action={saveEsrrAnswers}
                      >
                        {t('saveEsrrFormConfirmation')}
                      </ConfirmModal>
                    </>
                  );
                }}
              </Formik>
            </Section>
          </Row>
        )}
      </SpinnerArea>
    </Container>
  );
};

export default EsrrQuestionForm;
